import React from "react";
import { useLocation, Link, useNavigate, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useLang } from "../context/LanguageContext";
import { T, pick } from "../lib/translations";
import { GUIDES, GUIDE_ORDER, getGuideBySlug } from "../lib/guides";
import { ALL_CITIES } from "../lib/relatedContent";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { ArrowRight, Phone, Scale, HeartHandshake, Clock, BookOpen, Check, AlertTriangle, ExternalLink, ShieldCheck, MapPin } from "lucide-react";

const SITE_URL = "https://myrihai.com";
const ICONS = { Scale, HeartHandshake, Clock, BookOpen };

export default function GuidePage() {
  const loc = useLocation();
  const navigate = useNavigate();
  const { lang } = useLang();
  const isHub = loc.pathname === "/guide" || loc.pathname === "/guide/";
  const slug = loc.pathname.replace(/^\/guide\/?/, "").replace(/\/$/, "");

  // ---------- HUB (redirects to unified Resources hub) ----------
  if (isHub) {
    return <Navigate to="/blog" replace />;
  }

  // ---------- HUB (index of guides) ----------
  if (false) {
    const heading =
      lang === "hi"
        ? "पहले समझिए। फिर वकील तय कीजिए।"
        : "Understand first. Then decide on a lawyer.";
    const sub =
      lang === "hi"
        ? "सरल भाषा, कोई मुंशी नहीं, कोई डर नहीं। ये गाइड हैं जो हम चाहते थे कि हमारे पास होतीं।"
        : "Plain language, no middlemen, no fear. These are the guides we wish we had.";
    return (
      <>
        <SEO
          title={heading}
          description={sub}
          path="/guide"
        />
        <Nav />
        <main className="pt-32 pb-20">
          <div className="container-x">
            <div className="max-w-3xl mb-14">
              <div className="overline mb-5">{lang === "hi" ? "Rihai गाइड्स" : "Rihai Guides"}</div>
              <h1 className="font-serif-display serif-textured text-5xl sm:text-6xl font-light leading-[1.05]" data-testid="guide-hub-h1">
                {heading}
              </h1>
              <p className="text-lg text-[#4A4640] leading-relaxed mt-6">{sub}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6" data-testid="guide-hub-grid">
              {GUIDE_ORDER.map((s) => {
                const g = GUIDES[s];
                const Icon = ICONS[g.icon] || BookOpen;
                return (
                  <Link
                    key={s}
                    to={`/guide/${s}`}
                    className="group bg-white border border-[#D8CDBA] rounded-3xl p-8 hover:-translate-y-1 hover:shadow-xl transition-all"
                    data-testid={`guide-card-${s}`}
                  >
                    <div className="w-12 h-12 rounded-full bg-[#2C4C3B]/10 text-[#2C4C3B] flex items-center justify-center mb-5">
                      <Icon size={20} strokeWidth={1.75} />
                    </div>
                    <h3 className="font-serif-display text-2xl font-medium text-[#1A1A1A] leading-tight mb-3 group-hover:text-[#2C4C3B] transition-colors">
                      {pick(g.title, lang)}
                    </h3>
                    <p className="text-sm text-[#4A4640] leading-relaxed line-clamp-3">{pick(g.intro, lang)}</p>
                    <div className="mt-5 inline-flex items-center gap-1 text-xs font-medium text-[#2C4C3B] group-hover:gap-2 transition-all">
                      {lang === "hi" ? "पढ़ें" : "Read the guide"} <ArrowRight size={14} />
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Verified lawyers by city */}
            <div className="mt-16 pt-10 border-t border-[#D8CDBA]" data-testid="guide-hub-cities">
              <div className="overline mb-4">
                {lang === "hi" ? "शहर के अनुसार सत्यापित वकील" : "Verified bail lawyers by city"}
              </div>
              <div className="flex flex-wrap gap-2">
                {ALL_CITIES.map((ct) => (
                  <Link
                    key={ct.slug}
                    to={`/${ct.slug}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#D8CDBA] px-4 py-2 text-sm text-[#4A4640] hover:border-[#2C4C3B] hover:text-[#2C4C3B] transition-colors"
                    data-testid={`guide-hub-city-${ct.slug}`}
                  >
                    <MapPin size={12} strokeWidth={2} /> {lang === "hi" ? ct.cityHindi : ct.city}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // ---------- INDIVIDUAL GUIDE ----------
  const g = getGuideBySlug(slug);
  if (!g) {
    return (
      <>
        <SEO title="Guide not found" path={loc.pathname} />
        <Nav />
        <main className="pt-40 pb-20 min-h-screen container-x">
          <h1 className="font-serif-display text-4xl mb-4">
            {lang === "hi" ? "गाइड नहीं मिली" : "Guide not found"}
          </h1>
          <button onClick={() => navigate("/guide")} className="btn-primary">
            {lang === "hi" ? "सभी गाइड्स" : "All guides"}
          </button>
        </main>
        <Footer />
      </>
    );
  }

  const Icon = ICONS[g.icon] || BookOpen;

  // FAQPage JSON-LD (GEO / rich results)
  const faqLD = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: g.faqs.map((f) => ({
      "@type": "Question",
      name: pick(f.q, lang),
      acceptedAnswer: { "@type": "Answer", text: pick(f.a, lang) },
    })),
  };
  // Article JSON-LD
  const articleLD = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: pick(g.title, lang),
    description: pick(g.metaDescription, lang),
    author: { "@type": "Organization", name: "Rihai" },
    publisher: {
      "@type": "Organization",
      name: "Rihai",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/rihai-logo.png` },
    },
    datePublished: `${g.lastUpdated}T00:00:00+05:30`,
    dateModified: `${g.lastUpdated}T00:00:00+05:30`,
    inLanguage: lang === "hi" ? "hi-IN" : "en-IN",
    mainEntityOfPage: `${SITE_URL}/guide/${g.slug}`,
    reviewedBy: { "@type": "Organization", name: pick(g.reviewedBy, lang) },
  };

  return (
    <>
      <SEO
        title={pick(g.metaTitle, lang)}
        description={pick(g.metaDescription, lang)}
        path={`/guide/${g.slug}`}
        type="article"
        article={{
          publishedAt: `${g.lastUpdated}T00:00:00+05:30`,
          modifiedAt: `${g.lastUpdated}T00:00:00+05:30`,
          author: "Rihai",
          category: "Guide",
        }}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqLD)}</script>
        <script type="application/ld+json">{JSON.stringify(articleLD)}</script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Guides", item: `${SITE_URL}/guide` },
              { "@type": "ListItem", position: 3, name: pick(g.title, lang), item: `${SITE_URL}/guide/${g.slug}` },
            ],
          })}
        </script>
        {/* HowTo JSON-LD — emitted where the guide contains genuine sequential steps */}
        {g.timeline && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              name: pick(g.title, lang),
              description: pick(g.metaDescription, lang),
              step: g.timeline.map((t, i) => ({
                "@type": "HowToStep",
                position: i + 1,
                name: pick(t.day, lang),
                text: pick(t.event, lang),
              })),
            })}
          </script>
        )}
      </Helmet>

      <Nav />
      <main className="pt-32 pb-20">
        <div className="container-x max-w-3xl">
          <div className="flex items-center gap-3 mb-5">
            <Icon size={16} className="text-[#C46B2F]" strokeWidth={1.75} />
            <span className="overline">{lang === "hi" ? "Rihai गाइड" : "Rihai Guide"}</span>
          </div>
          <h1 className="font-serif-display serif-textured text-4xl sm:text-5xl font-light leading-[1.1] mb-6" data-testid="guide-h1">
            {pick(g.title, lang)}
          </h1>
          <div className="flex flex-wrap gap-4 text-xs text-[#736F66] mb-8" data-testid="guide-meta">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} /> {pick(g.reviewedBy, lang)}</span>
            <span>· {lang === "hi" ? "अंतिम अद्यतन" : "Last updated"}: {g.lastUpdated}</span>
          </div>

          <p className="text-lg text-[#4A4640] leading-relaxed mb-10" data-testid="guide-intro">
            {pick(g.intro, lang)}
          </p>

          {/* Key points — TL;DR for AI / GEO */}
          {g.keyPoints && (
          <div className="rounded-3xl bg-[#2C4C3B] text-white p-8 mb-14" data-testid="guide-keypoints">
            <div className="overline text-[#D8CDBA] mb-4">
              {lang === "hi" ? "जल्दी में हैं? यही जान लीजिए" : "In a hurry? Know this first"}
            </div>
            <ul className="space-y-3">
              {pick(g.keyPoints, lang).map((k, i) => (
                <li key={i} className="flex items-start gap-3 text-[#D8CDBA] leading-relaxed">
                  <Check size={16} className="text-[#E2762A] mt-1 flex-shrink-0" strokeWidth={2.2} />
                  <span>{k}</span>
                </li>
              ))}
            </ul>
          </div>
          )}

          {/* Rich sections */}
          {(g.sections || []).map((s, i) => (
            <section key={i} className="mb-12" data-testid={`guide-section-${i}`}>
              <h2 className="font-serif-display text-3xl font-medium leading-tight mb-6 text-[#1A1A1A]">
                {pick(s.h, lang)}
              </h2>
              {s.items && (
                <dl className="space-y-5">
                  {s.items.map((it, j) => (
                    <div key={j}>
                      <dt className="font-serif-display text-xl text-[#2C4C3B] mb-1">{pick(it.k, lang)}</dt>
                      <dd className="text-[#4A4640] leading-relaxed">{pick(it.v, lang)}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {s.list && (
                <ul className="space-y-3">
                  {pick(s.list, lang).map((x, j) => (
                    <li key={j} className="flex items-start gap-3 text-[#4A4640] leading-relaxed">
                      <span className="text-[#C46B2F] mt-1.5 w-1.5 h-1.5 rounded-full bg-[#C46B2F] flex-shrink-0" />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {/* Cost table */}
          {g.costTable && (
            <section className="mb-12" data-testid="guide-costtable">
              <h2 className="font-serif-display text-3xl font-medium leading-tight mb-6">
                {lang === "hi" ? "असली फ़ीस रेंज (2026)" : "Real fee ranges (2026)"}
              </h2>
              <div className="overflow-x-auto rounded-2xl border border-[#D8CDBA]">
                <table className="w-full text-sm">
                  <thead className="bg-[#EAE6DF] text-[#4A4640]">
                    <tr>
                      <th className="text-left p-4">{lang === "hi" ? "आइटम" : "Item"}</th>
                      <th className="text-left p-4">{lang === "hi" ? "रेंज" : "Range"}</th>
                      <th className="text-left p-4">{lang === "hi" ? "टिप्पणी" : "Note"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.costTable.map((row, i) => (
                      <tr key={i} className="border-t border-[#D8CDBA]">
                        <td className="p-4 text-[#1A1A1A]">{pick(row.item, lang)}</td>
                        <td className="p-4 font-serif-display text-[#2C4C3B] font-medium">{row.range}</td>
                        <td className="p-4 text-[#736F66] text-xs">{pick(row.note, lang)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Don't pay / bribery list */}
          {g.dontPay && (
            <section className="mb-12" data-testid="guide-dontpay">
              <div className="rounded-3xl bg-[#1F3A2C] text-white p-8 md:p-10">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle size={18} className="text-[#E2762A]" strokeWidth={2} />
                  <h2 className="font-serif-display text-2xl md:text-3xl font-medium leading-tight">
                    {pick(g.dontPay.title, lang)}
                  </h2>
                </div>
                <p className="text-[#D8CDBA] leading-relaxed mb-8">{pick(g.dontPay.intro, lang)}</p>
                <div className="space-y-5">
                  {g.dontPay.items.map((it, i) => (
                    <div key={i} className="border-t border-white/10 pt-5 first:border-t-0 first:pt-0" data-testid={`guide-dontpay-${i}`}>
                      <div className="flex flex-wrap justify-between items-baseline gap-2 mb-1.5">
                        <div className="font-medium text-white">{pick(it.role, lang)}</div>
                        <div className="font-serif-display text-[#E2762A]">{it.amount}</div>
                      </div>
                      <p className="text-sm text-[#D8CDBA] leading-relaxed">{pick(it.why, lang)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Timeline steps */}
          {g.timeline && (
            <section className="mb-12" data-testid="guide-timeline">
              <h2 className="font-serif-display text-3xl font-medium leading-tight mb-6">
                {lang === "hi" ? "असली समय-रेखा" : "The actual timeline"}
              </h2>
              <ol className="space-y-6 relative border-l-2 border-[#D8CDBA] pl-6">
                {g.timeline.map((t, i) => (
                  <li key={i} className="relative" data-testid={`guide-tl-${i}`}>
                    <span className="absolute -left-[31px] w-4 h-4 rounded-full bg-[#E2762A] border-4 border-[#FAF9F6]" />
                    <div className="text-xs uppercase tracking-widest text-[#C46B2F] mb-1">{pick(t.day, lang)}</div>
                    <p className="text-[#4A4640] leading-relaxed">{pick(t.event, lang)}</p>
                  </li>
                ))}
              </ol>
              {g.delays && (
                <div className="mt-10 rounded-2xl border border-[#D8CDBA] p-6">
                  <div className="overline mb-3">{pick(g.delays.title, lang)}</div>
                  <ul className="space-y-2">
                    {pick(g.delays.items, lang).map((x, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#4A4640]">
                        <span className="text-[#736F66] mt-2 w-1 h-1 rounded-full bg-[#736F66] flex-shrink-0" />
                        <span>{x}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}

          {/* Glossary */}
          {g.glossary && (
            <section className="mb-12" data-testid="guide-glossary">
              <h2 className="font-serif-display text-3xl font-medium leading-tight mb-6">
                {lang === "hi" ? "शब्दकोश" : "The glossary"}
              </h2>
              <dl className="divide-y divide-[#D8CDBA]">
                {g.glossary.map((row, i) => (
                  <div key={i} className="py-5" data-testid={`guide-gl-${i}`}>
                    <dt className="font-serif-display text-xl text-[#2C4C3B] mb-2">{pick(row.term, lang)}</dt>
                    <dd className="text-[#4A4640] leading-relaxed">{pick(row.def, lang)}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {/* FAQ */}
          <section className="mb-12" data-testid="guide-faq">
            <h2 className="font-serif-display text-3xl font-medium leading-tight mb-6">
              {lang === "hi" ? "अक्सर पूछे जाने वाले सवाल" : "Frequently asked questions"}
            </h2>
            <Accordion type="single" collapsible>
              {g.faqs.map((f, i) => (
                <AccordionItem key={i} value={`g-faq-${i}`} className="border-b border-[#D8CDBA]">
                  <AccordionTrigger className="text-left font-serif-display text-xl font-medium py-5 hover:no-underline" data-testid={`guide-faq-q-${i}`}>
                    {pick(f.q, lang)}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#4A4640] leading-relaxed pb-5" data-testid={`guide-faq-a-${i}`}>
                    {pick(f.a, lang)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* Sources */}
          <section className="mb-14" data-testid="guide-sources">
            <div className="overline mb-3">{lang === "hi" ? "स्रोत" : "Sources"}</div>
            <ul className="space-y-2 text-sm text-[#4A4640]">
              {g.sources.map((s, i) => (
                <li key={i} className="flex items-start gap-2">
                  <ExternalLink size={12} className="mt-1 text-[#736F66] flex-shrink-0" />
                  <a href={s.url} target="_blank" rel="noreferrer" className="hover:text-[#2C4C3B] underline underline-offset-2">
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </section>

          {/* CTA */}
          <div className="rounded-3xl bg-[#2C4C3B] text-white p-10 md:p-12 relative overflow-hidden" data-testid="guide-cta">
            <div className="grain" />
            <div className="tricolor-band tricolor-band-thick max-w-[100px] mb-6" />
            <h3 className="font-serif-display text-3xl md:text-4xl font-light leading-tight mb-3 relative">
              {lang === "hi" ? "अब जब समझ आ गया — क्या आगे बढ़ें?" : "Now that you understand — ready to move?"}
            </h3>
            <p className="text-[#D8CDBA] mb-8 max-w-lg relative">
              {lang === "hi"
                ? "एक सत्यापित वकील एक कॉल दूर है। या पहले हमसे बात कीजिए।"
                : "A verified lawyer is one call away. Or talk to us first — no pressure."}
            </p>
            <div className="flex flex-wrap gap-3 relative">
              <Link to="/#lead" className="btn-primary !bg-white !text-[#2C4C3B] hover:!bg-[#EAE6DF]" data-testid="guide-cta-lead">
                {pick(T.hero.ctaPrimary, lang)} <ArrowRight size={16} />
              </Link>
              <a href={`tel:${T.phone.number}`} className="btn-secondary !border-white !text-white hover:!bg-white hover:!text-[#2C4C3B]" data-testid="guide-cta-call">
                <Phone size={16} strokeWidth={2} /> {T.phone.display}
              </a>
            </div>
          </div>

          {/* Cross-links to other guides */}
          <div className="mt-14 pt-10 border-t border-[#D8CDBA]">
            <div className="overline mb-4">{lang === "hi" ? "और गाइड्स" : "More guides"}</div>
            <div className="grid sm:grid-cols-3 gap-3">
              {GUIDE_ORDER.filter((s) => s !== g.slug).map((s) => (
                <Link
                  key={s}
                  to={`/guide/${s}`}
                  className="rounded-2xl border border-[#D8CDBA] p-4 hover:border-[#2C4C3B] transition text-sm font-medium text-[#1A1A1A]"
                  data-testid={`guide-related-${s}`}
                >
                  {pick(GUIDES[s].title, lang)}
                </Link>
              ))}
            </div>
          </div>

          {/* Verified lawyers by city */}
          <div className="mt-10" data-testid="guide-cities">
            <div className="overline mb-4">
              {lang === "hi" ? "शहर के अनुसार सत्यापित वकील" : "Verified bail lawyers by city"}
            </div>
            <div className="flex flex-wrap gap-2">
              {ALL_CITIES.map((ct) => (
                <Link
                  key={ct.slug}
                  to={`/${ct.slug}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#D8CDBA] px-4 py-2 text-sm text-[#4A4640] hover:border-[#2C4C3B] hover:text-[#2C4C3B] transition-colors"
                  data-testid={`guide-city-${ct.slug}`}
                >
                  <MapPin size={12} strokeWidth={2} /> {lang === "hi" ? ct.cityHindi : ct.city}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
